import { ApolloCache } from '@apollo/client';
import { Box, Grid, MenuItem, Select, styled } from '@mui/material';
import ComponentAutocomplete from 'FeatureModules/ComponentChain/Components/Forms/ComponentAutocomplete';
import QuestionnaireSelection from 'FeatureModules/ComponentChain/Components/Forms/QuestionnaireSelection';
import LimitedTextField from 'Components/Forms/LimitedTextField';
import usePartnerRequestActions from 'FeatureModules/Partner/Components/PartnerRequests/usePartnerRequestActions';
import { DialogInner } from 'FeatureModules/Partner/Components/Partners.styles';
import useDialog from 'hooks/useDialog';
import { useLogEvent } from 'hooks/useLogEvent';
import useMessages from 'hooks/useMessages';
import { PartnerRequestSchema } from 'FeatureModules/Partner/schemas/partners.schema';
import { DialogDefault, FieldWrapper, ThemeButton, ThemeTypography } from 'DesignSystem';
import HTMLEditorField from 'DesignSystem/Inputs/HTMLEditorField/HTMLEditorField';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { REQUEST_ICON_NAME } from 'DesignSystem/utils/designSystemUtils';
import { useFormik } from 'formik';
import React, { FC, useState } from 'react';
import { IComponentItem } from 'FeatureModules/ComponentChain/types/component.types';
import { IDefaultDialogProps } from 'types/dialog.types';
import {
  EPartnerRequestDirection,
  EPartnerRequestStatus,
  ERequestType,
  IBasePartnerCompany,
  IPartnerRequest,
  IPartnerRequestForm,
  TRequestType,
} from 'FeatureModules/Partner/types/partner.types';
import { IBaseQuestionnaire } from 'FeatureModules/Questionnaire/types/questionnaire.types';
import PartnersAutocomplete from '../Form/PartnersAutocomplete';
import FlexBox from 'Components/FlexBox';
import PartnerAutocomplete from '../Form/PartnerAutocomplete';

export interface IPartnerRequestDialogProps extends IDefaultDialogProps {
  initialRequest?: IPartnerRequest;
  requestedToCompany?: IBasePartnerCompany;
  /**
   * Preselects & disables the type selection.
   */
  requestType?: TRequestType;
  /**
   * Preselects the questionnaire for self-assessment requests. Only applicable if `requestType` is `SELF_ASSESSMENT`.
   * Warning: Can be overwritten by `initialRequest.questionnaireId` if `initialRequest` is provided.
   */
  questionnaireId?: string;
  onRequestCreated?: (request: IPartnerRequest) => void;
  onUpdateCache?: (cache: ApolloCache<unknown>, request: IPartnerRequest) => void;
}

export const PartnerImage = styled('img')(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: 4,
  objectFit: 'cover',
  marginRight: theme.spacing(2),
  border: `1px solid ${theme.custom.themeColors.grayScale[20]}`,
}));

const AutoCompleteWrapper = styled(Box)(({ theme }) => ({
  flex: 1,

  '& .MuiOutlinedInput-input': {
    padding: '4px 6px !important',
  },
}));

const ComponentContainer = styled(Grid)(({ theme }) => ({
  '&.MuiGrid-root': {
    paddingTop: theme.spacing(1.5),
  },
}));

const SelectionContainer = styled(Box)(({ theme }) => ({
  background: theme.custom.colors.backgroundLight,
  borderRadius: 4,
  padding: 12,
}));

const PartnerRequestDialog: FC<IPartnerRequestDialogProps> = ({
  open,
  requestedToCompany,
  initialRequest,
  requestType,
  questionnaireId,
  onRequestCreated,
  onUpdateCache,
  onClose,
}) => {
  const [selectedPartners, setSelectedPartners] = useState<IBasePartnerCompany[]>([]);
  const requestedToCompanyId = requestedToCompany?.id;
  const { setErrorMessage, setSuccessMessage } = useMessages();
  const { loading, createPartnerRequest, updatePartnerRequest } = usePartnerRequestActions();
  const { openDialog, closeDialog } = useDialog();
  const { logEvent } = useLogEvent();

  const isCreateRequest = !initialRequest;

  const handleSubmitRequest = (values: IPartnerRequestForm) => {
    if (isCreateRequest) {
      logEvent('PARTNER_REQUEST_CREATED', { requestType: values.requestType });

      if (values.requestType === ERequestType.QUESTIONNAIRE && selectedPartners.length > 1) {
        // TODO: Fix this flow to handle multiple partners for other request types as well
        // Do that in a promise all and therefore handle errors/successes better and clean up this component
        selectedPartners.forEach(partner => {
          createPartnerRequest({
            variables: {
              input: {
                requestTitle: values.requestTitle,
                requestMessage: values.requestMessage,
                requestedToCompanyId: partner.id,
                requestType: values.requestType,
                ...(values.componentId && { componentId: values.componentId }),
                ...(values.questionnaireId && { questionnaireId: values.questionnaireId }),
              },
            },
            onError: error => {
              if (
                error.graphQLErrors.find(e => e.message === 'QUESTIONNAIRE_RESPONSE_ALREADY_EXISTS')
              ) {
                setErrorMessage(
                  'A request for this questionnaire and one selected partner already exists.'
                );
              } else {
                setErrorMessage(
                  'Something went wrong sending the request. Please try again later.'
                );
              }
            },
            onCompleted: data => {
              setSuccessMessage('The request has been sent to your partner');
              if (onRequestCreated) {
                onRequestCreated(data.createPartnerRequest.partnerRequest);
              }
              onClose?.();
            },
            update: (cache, { data }) => {
              if (onUpdateCache && data) {
                onUpdateCache(cache, data?.createPartnerRequest.partnerRequest);
              }
            },
          });
        });
      } else {
        createPartnerRequest({
          variables: {
            input: {
              requestTitle: values.requestTitle,
              requestMessage: values.requestMessage,
              requestedToCompanyId:
                values.requestedToCompanyId ||
                requestedToCompanyId ||
                (selectedPartners?.[0]?.id as string),
              requestType: values.requestType,
              ...(values.componentId && { componentId: values.componentId }),
              ...(values.questionnaireId && { questionnaireId: values.questionnaireId }),
            },
          },
          onError: error => {
            if (
              error.graphQLErrors.find(e => e.message === 'QUESTIONNAIRE_RESPONSE_ALREADY_EXISTS')
            ) {
              setErrorMessage(
                'A request for this questionnaire and one selected partner already exists.'
              );
            } else {
              setErrorMessage('Something went wrong sending the request. Please try again later.');
            }
          },
          onCompleted: data => {
            setSuccessMessage('The request has been sent to your partner');
            if (onRequestCreated) {
              onRequestCreated(data.createPartnerRequest.partnerRequest);
            }
            onClose?.();
          },
          update: (cache, { data }) => {
            if (onUpdateCache && data) {
              onUpdateCache(cache, data?.createPartnerRequest.partnerRequest);
            }
          },
        });
      }
    } else {
      updatePartnerRequest({
        variables: {
          id: initialRequest.id,
          input: {
            requestStatus: EPartnerRequestStatus.EDITED,
            documentId: undefined,
            requestReplyMessage: undefined,
            requestMessage: values.requestMessage,
          },
        },
        onError: () =>
          setErrorMessage('Something went wrong updating the request. Please try again later.'),
        onCompleted: () => {
          setSuccessMessage('The request has been updated and was sent to your partner');
          closeDialog();
        },
        update: (cache, { data }) => {
          if (onUpdateCache && data) {
            onUpdateCache(cache, data?.updatePartnerRequest.partnerRequest);
          }
        },
        refetchQueries: ['GetPartnerRequests', 'GetPartnerOverview'],
      });
    }
  };

  const formikBag = useFormik<IPartnerRequestForm>({
    initialValues:
      initialRequest && requestedToCompanyId
        ? {
            requestTitle: initialRequest.requestTitle,
            requestMessage: initialRequest.requestMessage,
            requestType: requestType || initialRequest.requestType,
            componentId: initialRequest?.componentId,
            requestedToCompanyId: requestedToCompanyId,
            questionnaireId: initialRequest.questionnaireId || questionnaireId,
          }
        : ({
            ...PartnerRequestSchema.default(),
            ...(requestType ? { requestType } : {}),
            ...(questionnaireId ? { questionnaireId } : {}),
          } as IPartnerRequestForm),
    onSubmit: handleSubmitRequest,
    validationSchema: PartnerRequestSchema,
    validateOnMount: true,
    validateOnChange: true,
  });

  const { values, handleSubmit, isValid, setFieldValue, errors, validateForm } = formikBag;

  const handleSelectComponent = (component: IComponentItem | null) => {
    setFieldValue('componentId', component?.id);

    // Selecting the component automatically suggests a description and title, if empty.
    if (values.requestType === ERequestType.CHAIN && component) {
      if (!values.requestTitle && component) {
        setFieldValue('requestTitle', `${component.title} chain mapping`);
      }
      if (!values.requestMessage && component) {
        setFieldValue(
          'requestMessage',
          `We are currently working on mapping our supply chain to enhance transparency and traceability. Could you please assist us by providing detailed information on the ‘${component.title}’ chain, including the chain steps, activities, involved partners, and locations?`
        );
      }
    }
  };

  const handleSelectQuestionnaire = (questionnaire: IBaseQuestionnaire | null) => {
    setFieldValue('questionnaireId', questionnaire?.id);
    // Selecting the component automatically suggests a description and title, if empty.
    if (values.requestType === ERequestType.QUESTIONNAIRE && questionnaire) {
      if (!values.requestTitle && questionnaire) {
        setFieldValue('requestTitle', questionnaire.title);
      }
      if (!values.requestMessage && questionnaire) {
        setFieldValue(
          'requestMessage',
          `We are currently conducting efforts of compliance. Could you please respond to all questions of this assessment and confirm with uploaded documents when necessary.`
        );
      }
    }
  };

  const handlePreviewQuestionnaire = () => {
    if (values.questionnaireId) {
      openDialog({
        type: 'PREVIEW_QUESTIONNAIRE',
        props: {
          questionnaireId: values.questionnaireId,
        },
      });
    }
  };

  const disableButton = !isValid || (!requestedToCompanyId && !selectedPartners.length);

  return (
    <DialogDefault
      open={open}
      iconName={REQUEST_ICON_NAME[values.requestType]}
      title={initialRequest ? 'Edit request' : 'New request'}
      onClose={onClose}
      maxWidth="sm"
    >
      <form onSubmit={handleSubmit}>
        <Box p={3}>
          <DialogInner container minHeight={300} spacing={2}>
            <Grid item xs={6}>
              <FieldWrapper
                label={
                  initialRequest?.direction === EPartnerRequestDirection.INCOMING
                    ? 'Request from'
                    : 'Request to'
                }
              >
                {!!requestedToCompany ? (
                  <Box maxHeight={33} display="flex" alignItems="center">
                    {requestedToCompany.logo && (
                      <PartnerImage
                        alt={`Logo of ${requestedToCompany.name}`}
                        src={requestedToCompany.logo?.url}
                      />
                    )}
                    <ThemeTypography variant="BODY_LARGE_BOLD">
                      {requestedToCompany.name}
                    </ThemeTypography>
                  </Box>
                ) : (
                  <AutoCompleteWrapper>
                    {values.requestType === ERequestType.QUESTIONNAIRE ? (
                      <PartnersAutocomplete
                        placeholder="Select a partners"
                        inviteNewPartner
                        // TODO: Invite and send request flow is currently only supported for document and chain requests
                        filterByRegisteredPartners
                        excludeOwnCompany
                        onPartnerSelectionChange={newPartnerSelection => {
                          setSelectedPartners(newPartnerSelection);
                          validateForm();
                        }}
                      />
                    ) : (
                      <PartnerAutocomplete
                        placeholder="Select a partner"
                        inviteNewPartner
                        filterByRegisteredPartners
                        excludeOwnCompany
                        onPartnerSelect={partner => {
                          setSelectedPartners(partner ? [partner] : []);
                          validateForm();
                        }}
                      />
                    )}
                  </AutoCompleteWrapper>
                )}
              </FieldWrapper>
            </Grid>

            <Grid item xs={6}>
              <FieldWrapper label="Request type">
                <Select
                  variant="outlined"
                  value={values['requestType']}
                  onChange={e => setFieldValue('requestType', e.target.value as ERequestType)}
                  inputProps={{
                    name: 'requestType',
                  }}
                  fullWidth
                  disabled={!isCreateRequest || !!requestType}
                  sx={{ height: 40 }}
                >
                  <MenuItem value={ERequestType.DOCUMENT}>Document</MenuItem>
                  <MenuItem value={ERequestType.CHAIN}>Chain mapping</MenuItem>
                  <MenuItem value={ERequestType.QUESTIONNAIRE}>Self-assessment</MenuItem>
                  <MenuItem disabled value={'Other'}>
                    More coming soon
                  </MenuItem>
                </Select>
              </FieldWrapper>
            </Grid>

            {/* For self-assessment requests the questionnaire selection */}
            {values.requestType === ERequestType.QUESTIONNAIRE && (
              <Grid item xs={12}>
                <ComponentContainer item xs={12}>
                  <SelectionContainer>
                    <FieldWrapper
                      label="Select questionnaire"
                      tooltip={{
                        helperText:
                          'Select which questionnaire you want to request your partner to complete. ',
                        variant: 'INFO',
                      }}
                    >
                      <QuestionnaireSelection
                        defaultSelectedQuestionnaireId={values['questionnaireId']}
                        size="small"
                        disabled={!isCreateRequest || !!questionnaireId}
                        onSelect={questionnaire => handleSelectQuestionnaire(questionnaire)}
                      />
                    </FieldWrapper>
                  </SelectionContainer>
                </ComponentContainer>
              </Grid>
            )}

            {/* For chain request the component selection */}
            {values.requestType === ERequestType.CHAIN && (
              <Grid item xs={12}>
                <ComponentContainer item xs={12}>
                  <SelectionContainer>
                    <FieldWrapper label="Select component">
                      <ComponentAutocomplete
                        defaultSelectedId={values['componentId']}
                        size="small"
                        company={requestedToCompany}
                        hideOtherPartnerComponents
                        disabled={!isCreateRequest}
                        onSelect={component => handleSelectComponent(component)}
                      />
                    </FieldWrapper>
                  </SelectionContainer>
                </ComponentContainer>
              </Grid>
            )}

            <Grid item xs={12}>
              <LimitedTextField
                label="Request title *"
                value={values['requestTitle']}
                name="requestTitle"
                onChange={e => setFieldValue('requestTitle', e.target.value)}
                errorMessage={errors['requestTitle']}
                fullWidth
                charLimit={50}
                disabled={!isCreateRequest}
              />
            </Grid>

            <Grid item xs={12}>
              <FieldWrapper label="Describe your request">
                <HTMLEditorField
                  minHeight={122}
                  value={values['requestMessage']}
                  name="requestMessage"
                  onChange={setFieldValue}
                />
              </FieldWrapper>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" mt={0.5} mb={1} gap={1}>
                <Icon name="lock" size="small" color="gray-40" />
                <ThemeTypography variant="BODY_MEDIUM" color="GRAY_60">
                  Avoid mentioning company names or other sensitive information.
                </ThemeTypography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between">
                <ThemeButton color="BLUE_ICE" size="large" onClick={onClose}>
                  Cancel
                </ThemeButton>

                <FlexBox gap={2}>
                  {values.requestType === ERequestType.QUESTIONNAIRE && values.questionnaireId && (
                    <ThemeButton size="large" color="BLUE_ICE" onClick={handlePreviewQuestionnaire}>
                      Preview questionnaire
                    </ThemeButton>
                  )}
                  <ThemeButton
                    loading={loading}
                    size="large"
                    disabled={disableButton}
                    type="submit"
                    tooltip={!disableButton ? undefined : 'Fill in all required fields'}
                  >
                    Send Request
                  </ThemeButton>
                </FlexBox>
              </Box>
            </Grid>
          </DialogInner>
        </Box>
      </form>
    </DialogDefault>
  );
};

export default PartnerRequestDialog;
